import type { GrowthBook } from "@growthbook/growthbook";
import type { GrowthbookFeatures } from "@shared/growthbook";
import { isbot } from "isbot";

type MockgrowthbookFeatures = {
  isOn: () => boolean;
  getFeatureValue: () => boolean | string;
  updateAttributes: () => void;
  init: () => void;
};

export default defineNuxtPlugin<{
  // Because this is a client only plugin we need to add this undefined check
  growthbook?: {
    features: Ref<GrowthBook<GrowthbookFeatures> | undefined>;
    loaded: Ref<boolean>;
    errored: Ref<boolean>;
    initialize: () => Promise<void>;
    identify: (options: {
      userId?: string | null;
      anonymousId?: string | null;
      publicIpAddress?: string | null;
    }) => Promise<void>;
  };
}>({
  name: "growthbook",
  dependsOn: ["amplify"],
  parallel: true,
  setup: (nuxtApp) => {
    const growthbookClientKey = nuxtApp.$config.public.growthbook.clientKey;
    const { getPlatformConfiguration } = usePlatformStore();
    const { tracking } = getPlatformConfiguration;

    const growthbookLoaded = ref(false);
    const growthbookErrored = ref(false);

    const growthbookFeatures = ref<
      GrowthBook<GrowthbookFeatures> | undefined | MockgrowthbookFeatures
    >(undefined);

    const { experimentViewed } = useAnalytics();
    const platformStore = usePlatformStore();

    if (!growthbookClientKey || !tracking.isEnabled) {
      // we are unable to load growthbook, set all feature flags to true
      growthbookLoaded.value = true;
    }

    const initialize = async () => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { GrowthBook } = await import("@growthbook/growthbook");
      const isCrawler = isbot(navigator.userAgent);

      growthbookFeatures.value = new GrowthBook({
        apiHost: "https://cdn.growthbook.io",
        clientKey: growthbookClientKey,
        enableDevMode: import.meta.dev,
        attributes: {
          userAgent: navigator.userAgent,
          url: window.location.origin,
          platformId: platformStore.tryGetPlatformConfiguration?.platformId,
          isCrawler,
        },

        trackingCallback: (experiment, result) => {
          experimentViewed({
            experimentId: experiment.key,
            variationId: result.key,
          });
        },
      });
    };

    const identify = async (options: {
      userId: string | null | undefined;
      anonymousId: string | null | undefined;
      publicIpAddress: string | null | undefined;
    }) => {
      if (!growthbookFeatures.value) {
        return;
      }

      try {
        await growthbookFeatures.value?.updateAttributes({
          // Check if the user id is set, otherwise use the anonymous id from segment
          id: options.userId ?? options.anonymousId,
          anonymousId: options.anonymousId,
          publicIpAddress: options.publicIpAddress,
        });

        await growthbookFeatures.value?.init();

        growthbookLoaded.value = true;
        growthbookErrored.value = false;
      } catch {
        growthbookLoaded.value = false;
        growthbookErrored.value = true;
      }
    };

    return nuxtApp.provide("growthbook", {
      features: growthbookFeatures,
      loaded: growthbookLoaded,
      errored: growthbookErrored,
      initialize,
      identify,
    });
  },
});
